import React, { useState, useEffect } from 'react';
import { apiClient } from '../../utils/api';
import { WorkflowRecord } from '../../types/admin';
import { DirectorWorkflowTree } from '../../components/DirectorWorkflowTree';
import { RefreshCw, Eye, Download, Copy, X, Image as ImageIcon, Save, RotateCcw } from 'lucide-react';

interface PreviewRecord {
  id: string;
  prompt: string;
  r2_object_key: string;
  model_used: string;
  created_at: string;
}

const TEMPLATE_LABELS: Record<string, { label: string; description: string }> = {
  t2i_system: { label: 'T2I 文生图系统提示词', description: '控制文生图卡片生成的系统提示词。支持变量：{{count}}（生成数量）、{{styleInstruction}}（风格指令）' },
  i2v_10s_system: { label: 'I2V 10秒模式系统提示词', description: '控制10秒视频分镜生成的系统提示词。用户输入通过 user message 传入。' },
  i2v_30s_system: { label: 'I2V 30秒模式系统提示词', description: '控制30秒视频分镜生成的系统提示词。系统会自动为第2/3段拼接视觉一致性前缀。' },
  variant_system: { label: '变体生成系统提示词', description: '控制卡片变体生成的系统提示词。' },
};

export const DirectorTab: React.FC = () => {
  const [subTab, setSubTab] = useState<'workflow' | 'preview' | 'templates'>('workflow');

  // Workflow 状态
  const [workflowHistory, setWorkflowHistory] = useState<WorkflowRecord[]>([]);
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [workflowPage, setWorkflowPage] = useState(1);
  const [workflowTotal, setWorkflowTotal] = useState(0);
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowRecord | null>(null);

  // Preview 状态
  const [previewHistory, setPreviewHistory] = useState<PreviewRecord[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPage, setPreviewPage] = useState(1);
  const [previewTotal, setPreviewTotal] = useState(0);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [previewModalImage, setPreviewModalImage] = useState<{
    url: string;
    prompt: string;
    id: string;
  } | null>(null);

  // Template 状态
  const [templates, setTemplates] = useState<Record<string, string>>({});
  const [templateEdits, setTemplateEdits] = useState<Record<string, string>>({});
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateSaving, setTemplateSaving] = useState<string | null>(null);

  useEffect(() => {
    if (subTab === 'workflow') {
      fetchWorkflowHistory();
    } else if (subTab === 'preview') {
      fetchPreviewHistory();
    } else if (subTab === 'templates') {
      fetchTemplates();
    }
  }, [subTab, workflowPage, previewPage]);

  const fetchWorkflowHistory = async () => {
    setWorkflowLoading(true);
    try {
      const response = await apiClient.get(
        `/api/admin/director-history?page=${workflowPage}&limit=20`
      );
      setWorkflowHistory(response.data.records || []);
      setWorkflowTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch workflow history:', error);
    } finally {
      setWorkflowLoading(false);
    }
  };

  const fetchPreviewHistory = async () => {
    setPreviewLoading(true);
    try {
      const response = await apiClient.get(
        `/api/admin/director-preview-history?page=${previewPage}&limit=50`
      );
      setPreviewHistory(response.data.records || []);
      setPreviewTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch preview history:', error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const toggleCardExpanded = (id: string) => {
    setExpandedCards((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleDownloadImage = async (id: string) => {
    try {
      const response = await fetch(
        `https://cloudflare-mutimodal-backend.steven-mei0814.workers.dev/files/${id}`
      );
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `preview_${id}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download image:', error);
    }
  };

  const handleCopyPrompt = (prompt: string) => {
    navigator.clipboard.writeText(prompt);
  };

  const fetchTemplates = async () => {
    setTemplateLoading(true);
    try {
      const response = await apiClient.get('/api/admin/director/prompt-templates');
      const data = response.data || {};
      setTemplates(data);
      setTemplateEdits(data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleSaveTemplate = async (key: string) => {
    const value = templateEdits[key];
    if (!value?.trim()) return;

    setTemplateSaving(key);
    try {
      await apiClient.put('/api/admin/director/prompt-templates', {
        template_key: key,
        template_value: value,
      });
      setTemplates((prev) => ({ ...prev, [key]: value }));
      alert('保存成功');
    } catch (error) {
      console.error('Failed to save template:', error);
      alert('保存失败');
    } finally {
      setTemplateSaving(null);
    }
  };

  const handleResetTemplate = async (key: string) => {
    if (!confirm(`确定要将「${TEMPLATE_LABELS[key]?.label}」恢复为代码默认值吗？`)) return;

    setTemplateSaving(key);
    try {
      await apiClient.delete(`/api/admin/director/prompt-templates/${key}`);
      setTemplates((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setTemplateEdits((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      alert('已恢复为默认值');
    } catch (error) {
      console.error('Failed to reset template:', error);
      alert('重置失败');
    } finally {
      setTemplateSaving(null);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white">Director 工作流</h2>

      {/* 子标签页切换 */}
      <div className="border-b border-cyan-900/30">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setSubTab('workflow')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              subTab === 'workflow'
                ? 'border-cyan-400 text-cyan-400'
                : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-cyan-900/30'
            }`}
          >
            AI导演工作台历史
          </button>
          <button
            onClick={() => setSubTab('preview')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              subTab === 'preview'
                ? 'border-cyan-400 text-cyan-400'
                : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-cyan-900/30'
            }`}
          >
            Image Preview历史
          </button>
          <button
            onClick={() => setSubTab('templates')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              subTab === 'templates'
                ? 'border-cyan-400 text-cyan-400'
                : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-cyan-900/30'
            }`}
          >
            提示词模板
          </button>
        </nav>
      </div>

      {/* Workflow 子标签 */}
      {subTab === 'workflow' && (
        <div className="bg-[#161b22] rounded-xl border border-cyan-900/30 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-white">AI导演工作台历史</h3>
            <button
              onClick={fetchWorkflowHistory}
              disabled={workflowLoading}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded-md disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${workflowLoading ? 'animate-spin' : ''}`} />
              <span>刷新</span>
            </button>
          </div>

          {workflowLoading ? (
            <div className="text-center py-12">
              <RefreshCw className="h-12 w-12 text-cyan-400 animate-spin mx-auto mb-4" />
              <p className="text-gray-400">加载中...</p>
            </div>
          ) : workflowHistory.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <p>暂无工作流记录</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* 表格列表 */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-cyan-900/30">
                  <thead className="bg-[#0d1117]">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        种子提示词
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        风格偏好
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        创建时间
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        操作
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-cyan-900/30">
                    {workflowHistory.map((record) => (
                      <tr key={record.id} className="hover:bg-cyan-900/10">
                        <td className="px-6 py-4 text-sm text-white">
                          <div className="max-w-md truncate" title={record.seed_prompt}>
                            {record.seed_prompt}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-400">
                          {record.style_preference || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-400 whitespace-nowrap">
                          {new Date(record.created_at).toLocaleString('zh-CN')}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <button
                            onClick={() => setSelectedWorkflow(record)}
                            className="text-cyan-400 hover:text-cyan-300 flex items-center space-x-1"
                          >
                            <Eye className="h-4 w-4" />
                            <span>查看详情</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* 分页 */}
              <div className="flex items-center justify-between pt-4 border-t border-cyan-900/30">
                <div className="text-sm text-gray-400">共 {workflowTotal} 条记录</div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setWorkflowPage(Math.max(1, workflowPage - 1))}
                    disabled={workflowPage === 1}
                    className="px-3 py-1 border border-cyan-900/30 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-cyan-900/10 text-gray-300"
                  >
                    上一页
                  </button>
                  <span className="text-sm text-gray-400">第 {workflowPage} 页</span>
                  <button
                    onClick={() => setWorkflowPage(workflowPage + 1)}
                    disabled={workflowHistory.length < 20}
                    className="px-3 py-1 border border-cyan-900/30 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-cyan-900/10 text-gray-300"
                  >
                    下一页
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Preview 子标签 */}
      {subTab === 'preview' && (
        <div className="bg-[#161b22] rounded-xl border border-cyan-900/30 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-white">Image Preview历史</h3>
            <button
              onClick={fetchPreviewHistory}
              disabled={previewLoading}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded-md disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${previewLoading ? 'animate-spin' : ''}`} />
              <span>刷新</span>
            </button>
          </div>

          {previewLoading ? (
            <div className="text-center py-12">
              <RefreshCw className="h-12 w-12 text-cyan-400 animate-spin mx-auto mb-4" />
              <p className="text-gray-400">加载中...</p>
            </div>
          ) : previewHistory.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <ImageIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>暂无预览记录</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* 网格布局 */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {previewHistory.map((record) => {
                  const isExpanded = expandedCards.has(record.id);
                  return (
                    <div
                      key={record.id}
                      className="bg-[#0d1117] rounded-xl overflow-hidden border border-cyan-900/30 hover:border-cyan-700/50 transition-all group"
                    >
                      {/* 缩略图 */}
                      <div
                        className="relative aspect-square cursor-pointer"
                        onClick={() => toggleCardExpanded(record.id)}
                      >
                        <img
                          src={`https://cloudflare-mutimodal-backend.steven-mei0814.workers.dev/files/${record.id}`}
                          alt={record.prompt.substring(0, 50)}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                          <div className="text-white opacity-0 group-hover:opacity-100 transition-opacity text-xs">
                            {isExpanded ? '收起' : '展开'}
                          </div>
                        </div>
                      </div>

                      {/* 信息和操作 */}
                      <div className="p-3">
                        {/* 详细信息 - 仅在展开时显示 */}
                        {isExpanded && (
                          <>
                            <p
                            className="text-xs text-gray-300 mb-2 line-clamp-3"
                              title={record.prompt}
                            >
                              {record.prompt}
                            </p>
                            <div className="flex items-center justify-between text-xs text-gray-400 mb-3">
                              <span className="truncate">{record.model_used}</span>
                              <span className="ml-2">
                                {new Date(record.created_at).toLocaleDateString('zh-CN')}
                              </span>
                            </div>
                          </>
                        )}

                        {/* 操作按钮 */}
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() =>
                              setPreviewModalImage({
                                url: `https://cloudflare-mutimodal-backend.steven-mei0814.workers.dev/files/${record.id}`,
                                prompt: record.prompt,
                                id: record.id,
                              })
                            }
                            className="flex-1 flex items-center justify-center px-2 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded transition-colors"
                            title="预览"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDownloadImage(record.id)}
                            className="flex-1 flex items-center justify-center px-2 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                            title="下载"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleCopyPrompt(record.prompt)}
                            className="flex-1 flex items-center justify-center px-2 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                            title="复制提示词"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* 分页 */}
              <div className="flex items-center justify-between pt-4 border-t border-cyan-900/30">
                <div className="text-sm text-gray-400">共 {previewTotal} 条记录</div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setPreviewPage(Math.max(1, previewPage - 1))}
                    disabled={previewPage === 1}
                    className="px-3 py-1 border border-cyan-900/30 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-cyan-900/10 text-gray-300"
                  >
                    上一页
                  </button>
                  <span className="text-sm text-gray-400">第 {previewPage} 页</span>
                  <button
                    onClick={() => setPreviewPage(previewPage + 1)}
                    disabled={previewHistory.length < 50}
                    className="px-3 py-1 border border-cyan-900/30 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-cyan-900/10 text-gray-300"
                  >
                    下一页
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Templates 子标签 */}
      {subTab === 'templates' && (
        <div className="bg-[#161b22] rounded-xl border border-cyan-900/30 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-white">提示词模板管理</h3>
              <p className="text-sm text-gray-400 mt-1">自定义 Director 各环节的系统提示词。留空则使用代码默认值。</p>
            </div>
            <button
              onClick={fetchTemplates}
              disabled={templateLoading}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded-md disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${templateLoading ? 'animate-spin' : ''}`} />
              <span>刷新</span>
            </button>
          </div>

          {templateLoading ? (
            <div className="text-center py-12">
              <RefreshCw className="h-12 w-12 text-cyan-400 animate-spin mx-auto mb-4" />
              <p className="text-gray-400">加载中...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(TEMPLATE_LABELS).map(([key, { label, description }]) => {
                const hasCustom = key in templates;
                const editValue = templateEdits[key] || '';
                const isSaving = templateSaving === key;
                const isDirty = editValue !== (templates[key] || '');

                return (
                  <div key={key} className="bg-[#0d1117] rounded-lg border border-cyan-900/30 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="text-sm font-semibold text-white flex items-center">
                          {label}
                          {hasCustom && (
                            <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-cyan-600 rounded">自定义</span>
                          )}
                          {!hasCustom && (
                            <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-gray-600 rounded">默认</span>
                          )}
                        </h4>
                        <p className="text-xs text-gray-400 mt-0.5">{description}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        {hasCustom && (
                          <button
                            onClick={() => handleResetTemplate(key)}
                            disabled={isSaving}
                            className="flex items-center space-x-1 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-md text-xs disabled:opacity-50"
                          >
                            <RotateCcw className="w-3 h-3" />
                            <span>恢复默认</span>
                          </button>
                        )}
                        <button
                          onClick={() => handleSaveTemplate(key)}
                          disabled={isSaving || !editValue.trim() || !isDirty}
                          className="flex items-center space-x-1 px-3 py-1.5 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded-md text-xs disabled:opacity-50"
                        >
                          <Save className="w-3 h-3" />
                          <span>{isSaving ? '保存中...' : '保存'}</span>
                        </button>
                      </div>
                    </div>
                    <textarea
                      value={editValue}
                      onChange={(e) => setTemplateEdits((prev) => ({ ...prev, [key]: e.target.value }))}
                      placeholder="留空使用代码默认值。输入自定义提示词模板覆盖默认行为。"
                      className="w-full px-3 py-2 bg-[#161b22] border border-cyan-900/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 text-gray-200 text-xs font-mono"
                      rows={8}
                      disabled={isSaving}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* 预览图片 Modal */}
      {previewModalImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={() => setPreviewModalImage(null)}
        >
          <button
            onClick={() => setPreviewModalImage(null)}
            className="absolute top-4 right-4 p-2 bg-gray-800/80 hover:bg-gray-700 rounded-full transition-colors z-10"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <div className="relative max-w-5xl max-h-[90vh] flex flex-col">
            <img
              src={previewModalImage.url}
              alt={previewModalImage.prompt}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="mt-4 p-4 bg-gray-800/90 rounded-lg max-w-3xl mx-auto">
              <p className="text-sm text-gray-300 mb-3">{previewModalImage.prompt}</p>
              <div className="flex items-center space-x-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownloadImage(previewModalImage.id);
                  }}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span>下载图片</span>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyPrompt(previewModalImage.prompt);
                  }}
                  className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md transition-colors"
                >
                  <Copy className="w-4 h-4" />
                  <span>复制提示词</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 工作流详情 Modal */}
      {selectedWorkflow && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={() => setSelectedWorkflow(null)}
        >
          <div
            className="relative w-full max-w-7xl max-h-[90vh] bg-[#161b22] rounded-xl border border-cyan-900/30 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedWorkflow(null)}
              className="absolute top-4 right-4 z-20 p-2 bg-gray-800/80 hover:bg-gray-700 rounded-full transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            <div className="p-6">
              <h3 className="text-xl font-bold text-white mb-4">工作流详情</h3>
              <DirectorWorkflowTree record={selectedWorkflow} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
